/**
 * Dialog and modal helpers
 */

import { escapeHtml } from '../utils.js';
import { setSuspendUpdates } from '../globals.js';

// jQuery UI dialog types
declare global {
  interface JQuery {
    dialog(options: JQueryUI.DialogOptions): JQuery;
    dialog(method: 'close' | 'open' | 'destroy'): JQuery;
  }
}

/**
 * Show loading overlay
 */
export function declareLoading(): void {
  setSuspendUpdates(true);
  $('#loading').show();
}

/**
 * Hide loading overlay
 */
export function declareLoaded(): void {
  setSuspendUpdates(false);
  $('#loading').hide();
  $('#content').show();
}

/**
 * Show a notification dialog
 */
export function notificationDialog(msg: string, allowHtml = false): void {
  const content = allowHtml ? msg : escapeHtml(msg);
  $('<div></div>')
    .appendTo('body')
    .html(`<div><h4>${content}</h4></div>`)
    .dialog({
      modal: true,
      title: 'Notice',
      zIndex: 10000,
      autoOpen: true,
      width: 'auto',
      resizable: false,
      buttons: {
        Okay: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Show a confirmation dialog
 */
export function confirmDialog(
  msg: string,
  yesCallback?: () => void,
  noCallback?: () => void,
  allowHtml = false
): void {
  const content = allowHtml ? msg : escapeHtml(msg);
  $('<div></div>')
    .appendTo('body')
    .html(`<div><h4>${content}</h4></div>`)
    .dialog({
      modal: true,
      title: 'Confirm',
      zIndex: 10000,
      autoOpen: true,
      width: 'auto',
      resizable: false,
      buttons: {
        Yes: function () {
          if (yesCallback) yesCallback();
          $(this).dialog('close');
        },
        No: function () {
          if (noCallback) noCallback();
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Show an error dialog with optional technical details
 */
export function errorDialog(title: string, message: string, details?: string): void {
  let content = '<div style="max-width: 500px;">';
  content += `<p style="margin-bottom: 15px;"><strong>${escapeHtml(message)}</strong></p>`;

  if (details && details.trim()) {
    content += '<details style="margin-top: 10px;">';
    content += '<summary style="cursor: pointer; color: #666;">Show technical details</summary>';
    content += '<pre style="background: #f5f5f5; padding: 10px; margin-top: 10px; max-height: 200px; overflow: auto; font-size: 11px; white-space: pre-wrap; word-wrap: break-word;">';
    content += escapeHtml(details);
    content += '</pre></details>';
  }
  content += '</div>';

  $('<div></div>')
    .appendTo('body')
    .html(content)
    .dialog({
      modal: true,
      title: title,
      zIndex: 10000,
      autoOpen: true,
      width: 550,
      resizable: false,
      buttons: {
        OK: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Show a form dialog
 */
export function formDialog(
  title: string,
  content: string,
  onSubmit: () => void | Promise<void>,
  options?: {
    submitLabel?: string;
    cancelLabel?: string;
    width?: number | 'auto';
    onOpen?: () => void;
  }
): void {
  $('<div></div>')
    .appendTo('body')
    .html(content)
    .dialog({
      modal: true,
      title: title,
      zIndex: 10000,
      autoOpen: true,
      width: options?.width ?? 'auto',
      resizable: false,
      buttons: {
        [options?.submitLabel ?? 'Submit']: async function () {
          try {
            await onSubmit();
            $(this).dialog('close');
          } catch (e) {
            // Keep dialog open on error
          }
        },
        [options?.cancelLabel ?? 'Cancel']: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
      open: options?.onOpen,
    });
}

/**
 * Show a waiting/countdown dialog
 */
export function waitingDialog(
  title: string,
  message: string,
  checkCallback: () => Promise<boolean>,
  intervalSeconds = 30
): void {
  let timer: ReturnType<typeof setInterval>;

  const doCountdown = (): void => {
    const secsEl = $('#secs');
    let secs = parseInt(secsEl.html() || '0', 10) - 1;
    if (secs <= 0) {
      checkCallback().then(done => {
        if (done) {
          clearInterval(timer);
          $('.ui-dialog-content').dialog('close');
          window.location.reload();
        } else {
          secsEl.html(String(intervalSeconds));
        }
      });
    } else {
      secsEl.html(String(secs));
    }
  };

  $('<div></div>')
    .appendTo('body')
    .html(`<div><h3>${message} Checking in <span id="secs">${intervalSeconds}</span> second(s)</h3></div>`)
    .dialog({
      modal: true,
      title: title,
      zIndex: 10000,
      autoOpen: true,
      width: 'auto',
      resizable: false,
      buttons: {
        'Check Now': function () {
          checkCallback().then(done => {
            if (done) {
              $(this).dialog('close');
              window.location.reload();
            }
          });
        },
      },
      close: function () {
        clearInterval(timer);
        $(this).remove();
      },
    });

  timer = setInterval(doCountdown, 1000);
}

/**
 * Show a paste dialog with a textarea for the user to Ctrl+V / Cmd+V into.
 *
 * This avoids the browser's Clipboard API permission prompt that
 * navigator.clipboard.readText() triggers.  The native paste event
 * into a focused textarea requires no special permissions.
 *
 * @param onPaste - called with the pasted text; dialog closes automatically
 */
export function pasteDialog(onPaste: (text: string) => void): void {
  const html = `
    <div>
      <p style="margin-bottom: 8px;">Press <kbd>Ctrl+V</kbd> (or <kbd>⌘V</kbd>) to paste:</p>
      <textarea id="paste-dialog-textarea" rows="4"
        style="width: 100%; resize: vertical; font-family: monospace; font-size: 13px;"></textarea>
    </div>
  `;

  const dlg = $('<div></div>')
    .appendTo('body')
    .html(html)
    .dialog({
      modal: true,
      title: 'Paste',
      zIndex: 10001,
      autoOpen: true,
      width: 400,
      resizable: false,
      buttons: {
        Send: function () {
          const text = ($('#paste-dialog-textarea').val() as string) || '';
          if (text) {
            onPaste(text);
          }
          $(this).dialog('close');
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
      open: function () {
        const textarea = document.getElementById('paste-dialog-textarea') as HTMLTextAreaElement;
        if (textarea) {
          textarea.focus();
          textarea.addEventListener('paste', () => {
            // Defer reading the value until the browser has inserted the pasted text
            setTimeout(() => {
              const text = textarea.value;
              if (text) {
                onPaste(text);
                dlg.dialog('close');
              }
            }, 0);
          });
        }
      },
    });
}
